import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { THUNDERDOME, CALYPSO, OLAF } from 'HubStyleTokens/colors';
import { GRID_BREAKPOINT_SMALL, GRID_BREAKPOINT_LARGE } from 'HubStyleTokens/sizes';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import H4 from 'UIComponents/elements/headings/H4';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UIBox from 'UIComponents/layout/UIBox';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITruncateString from 'UIComponents/text/UITruncateString';
import * as ScreenSizes from '../../layout/constants/ScreenSizes';
import MediaQuery from '../../layout/components/MediaQuery';
import MainSectionInner from '../../layout/components/MainSectionInner';
import SearchSelectWrapper from '../../search/components/SearchSelectWrapper';
import StyledHeaderGrid from './StyledHeaderGrid';
import StyledHeaderWrapper from './StyledHeaderWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledSubtitleText = styled.span.withConfig({
  displayName: "StorefrontHeader__StyledSubtitleText",
  componentId: "sc-1uilqpw-0"
})(["font-weight:600 !important;-webkit-font-smoothing:antialiased !important;-moz-osx-font-smoothing:grayscale !important;font-size:18px !important;line-height:normal !important;"]);
export const CustomStyleValues = {
  MAIN_SECTION_CLASSNAME: 'p-top-7',
  BACKGROUND_COLOR: THUNDERDOME,
  START_GRADIENT_COLOR: THUNDERDOME,
  END_GRADIENT_COLOR: CALYPSO
};

// this prevents the page from shifting when the header becomes sticky
const STICKY_BOTTOM_OFFSET = 46;
const StorefrontHeader = ({
  children,
  collapsedTitle,
  compact = false,
  customBreakpoints = {
    GRID_BREAKPOINT_MIN: GRID_BREAKPOINT_SMALL,
    GRID_BREAKPOINT_MAX: GRID_BREAKPOINT_LARGE
  },
  customStyle = CustomStyleValues,
  onStickyChange,
  renderedButtonRow,
  subtitle,
  title,
  testId,
  use = 'default'
}) => {
  // StyledHeaderWrapper gradient colors
  const {
    MAIN_SECTION_CLASSNAME,
    BACKGROUND_COLOR,
    GRADIENT_ANGLE,
    START_GRADIENT_COLOR,
    END_GRADIENT_COLOR,
    SEARCH_PADDING_BOTTOM
  } = customStyle;

  // StyledHeaderGrid breakpoints
  const {
    GRID_BREAKPOINT_MIN,
    GRID_BREAKPOINT_MAX
  } = customBreakpoints;
  const [isSticky, setIsSticky] = useState(compact);
  const mobileChildrenWrapperSize = use === 'collapsed' ? 2 : 10;
  const mainContainerRef = useRef(null);
  const searchBarRef = useRef(null);
  const isNewAppNav = document.body.classList.contains('sticky-global-toolbar');
  useEffect(() => {
    if (onStickyChange) {
      onStickyChange(SyntheticEvent(isSticky));
    }
    /*
    TODO - fix:
    In the interest of time I'm only including isSticky in the dependency array because
    if onStickyChange is defined in-line it changes with every render and causes this
    hook to run infinitely. When we re-refactor this component we can revisit this issue.
     */
  }, [isSticky]); // eslint-disable-line

  // set up sticky scroll behavior
  useEffect(() => {
    if (!mainContainerRef.current || !searchBarRef.current) return;
    const mainContainerTop = mainContainerRef.current.getBoundingClientRect().top;

    // distance between top of header and top of search bar
    const searchBarDistance = searchBarRef.current.getBoundingClientRect().top - mainContainerTop;

    // new app nav has its own sticky header, so we need to account for that
    const stickyThreshold = isNewAppNav ? -searchBarDistance + mainContainerTop : -searchBarDistance;

    // called every time the scroll position changes
    const scrollHandler = () => {
      const scrollPos = mainContainerRef.current.getBoundingClientRect().top;
      setIsSticky(scrollPos < stickyThreshold);
    };

    // register/unregister scroll handler
    window.addEventListener('scroll', scrollHandler, true);
    return () => window.removeEventListener('scroll', scrollHandler, true);

    // IMPORTANT NOTE: isNewAppNav may change over time as the page loads,
    // so it's important to recalculate the stickyThreshold when it changes
  }, [isNewAppNav]); // eslint-disable-line

  // set sticky to false when location changes (because we'll be back at the top)
  useEffect(() => {
    setIsSticky(false);
    if (onStickyChange) onStickyChange(SyntheticEvent(false));
  }, [window.location.search, window.location.href]); // eslint-disable-line

  return /*#__PURE__*/_jsxs(StyledHeaderWrapper, {
    className: isSticky ? "sticky" : "",
    "data-test-id": testId,
    backgroundColor: BACKGROUND_COLOR,
    gradientAngle: GRADIENT_ANGLE,
    innerRef: mainContainerRef,
    startGradientColor: START_GRADIENT_COLOR,
    endGradientColor: END_GRADIENT_COLOR,
    children: [!compact && use !== 'collapsed' && /*#__PURE__*/_jsx(MainSectionInner, {
      className: MAIN_SECTION_CLASSNAME,
      children: /*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        direction: "column",
        children: [renderedButtonRow && /*#__PURE__*/_jsx(UIBox, {
          alignSelf: "end",
          children: /*#__PURE__*/_jsx(MediaQuery, {
            minScreenSize: ScreenSizes.MD,
            children: renderedButtonRow
          })
        }), /*#__PURE__*/_jsx("span", {
          "data-test-id": "titleText",
          style: {
            textAlign: 'center',
            color: OLAF
          },
          children: title
        }), subtitle ? /*#__PURE__*/_jsx(StyledSubtitleText, {
          "data-test-id": "subtitleText",
          className: "m-bottom-8",
          style: {
            textAlign: 'center',
            color: OLAF,
            paddingBottom: isSticky ? STICKY_BOTTOM_OFFSET : 0
          },
          children: subtitle
        }) : null]
      })
    }), /*#__PURE__*/_jsx(SearchSelectWrapper, {
      collapsed: use === 'collapsed',
      isNewAppNav: isNewAppNav,
      ref: searchBarRef,
      isSticky: isSticky,
      gradientAngle: GRADIENT_ANGLE,
      startGradientColor: START_GRADIENT_COLOR,
      endGradientColor: END_GRADIENT_COLOR,
      paddingBottom: SEARCH_PADDING_BOTTOM,
      children: /*#__PURE__*/_jsx(MainSectionInner, {
        children: /*#__PURE__*/_jsxs(StyledHeaderGrid, {
          className: isSticky || use === 'collapsed' ? "sticky" : "",
          minBreakpoint: GRID_BREAKPOINT_MIN,
          maxBreakpoint: GRID_BREAKPOINT_MAX,
          children: [use === 'collapsed' && /*#__PURE__*/_jsx(MediaQuery, {
            minScreenSize: ScreenSizes.SM,
            alternate: /*#__PURE__*/_jsx(UIGridItem, {
              size: {
                xs: 10,
                md: 4,
                lg: 3
              },
              children: /*#__PURE__*/_jsx(H4, {
                "aria-level": 4,
                style: {
                  color: OLAF
                },
                className: "m-bottom-0 p-top-3",
                children: /*#__PURE__*/_jsx(UITruncateString, {
                  children: collapsedTitle
                })
              })
            }),
            children: /*#__PURE__*/_jsx(UIGridItem, {
              size: {
                xs: 2,
                md: 4,
                lg: 3
              },
              children: /*#__PURE__*/_jsx(UIMediaObject, {
                align: "baseline",
                className: "p-top-2",
                itemLeft: /*#__PURE__*/_jsx(UIIcon, {
                  name: "sprocket",
                  size: "xs",
                  color: OLAF
                }),
                children: /*#__PURE__*/_jsx(H4, {
                  "aria-level": 4,
                  style: {
                    color: OLAF
                  },
                  className: "m-bottom-0",
                  children: /*#__PURE__*/_jsx(MediaQuery, {
                    minScreenSize: ScreenSizes.MD,
                    children: /*#__PURE__*/_jsx(UITruncateString, {
                      children: collapsedTitle
                    })
                  })
                })
              })
            })
          }), isSticky && use !== 'collapsed' && /*#__PURE__*/_jsx(UIGridItem, {
            size: {
              xs: 2,
              md: 4,
              lg: 3
            },
            children: /*#__PURE__*/_jsx(UIMediaObject, {
              align: "baseline",
              className: "p-top-2",
              itemLeft: /*#__PURE__*/_jsx(UIIcon, {
                name: "sprocket",
                size: "xs",
                color: OLAF
              }),
              children: /*#__PURE__*/_jsx(H4, {
                "aria-level": 4,
                style: {
                  color: OLAF
                },
                className: "m-bottom-0",
                children: /*#__PURE__*/_jsx(MediaQuery, {
                  minScreenSize: ScreenSizes.MD,
                  children: /*#__PURE__*/_jsx(UITruncateString, {
                    children: collapsedTitle
                  })
                })
              })
            })
          }), /*#__PURE__*/_jsx(UIGridItem, {
            className: "p-x-0",
            size: isSticky || use === 'collapsed' ? {
              xs: mobileChildrenWrapperSize,
              sm: 10,
              md: 8,
              lg: 9
            } : 12,
            children: children
          })]
        })
      })
    })]
  });
};
export default StorefrontHeader;